import { wrapReducer } from "@client/content/lib/helper";
import { updateItem } from "@client/lib/array";
import { Status } from "@client/types";
import { type ReduxAction } from "@client/types/ReduxAction";
import {
  Actions,
  type AddToCartArgs,
  type FetchPageBeginActionData,
  type FetchPageSuccessActionData,
  type InitBeginActionData,
  type InitSuccessActionData,
  type SetFilterActionData
} from "./actions";
import { ProductList } from "./types";

const reducer = (state: ProductList.State, action: ReduxAction<any>): ProductList.State => {
  switch (action.type) {
    case Actions.InitBegin: {
      const { page } = action.data as InitBeginActionData;
      return {
        ...state,
        page,
        status: "Loading"
      };
    }
    case Actions.InitFailure: {
      return {
        ...state,
        status: "Ready"
      };
    }
    case Actions.InitSuccess: {
      const { cartUrl, categories, orders, items, shouldDisplayTax } = action.data as InitSuccessActionData;
      return {
        ...state,
        cartUrl,
        categories,
        hasProducts: !!items.length,
        orders,
        items: items.map((item) => ({ ...item, progressButton: null, isInCart: false })),
        shouldDisplayTax,
        status: "Ready"
      };
    }

    case Actions.AddToCartBegin: {
      const { productId, goToCheckout } = action.params as AddToCartArgs;
      return {
        ...state,
        items: updateItem(state.items, productId, (item) => ({
          ...item,
          progressButton: goToCheckout ? "buy" : "add"
        })),
        status: "AddingToCart"
      };
    }
    case Actions.AddToCartFailure: {
      const { productId } = action.params as AddToCartArgs;
      return {
        ...state,
        items: updateItem(state.items, productId, (item) => ({ ...item, progressButton: null })),
        status: "Ready"
      };
    }
    case Actions.AddToCartSuccess: {
      const { productId, goToCheckout } = action.params as AddToCartArgs;
      return {
        ...state,
        items: updateItem(state.items, productId, (item) => ({
          ...item,
          progressButton: goToCheckout ? "buy" : null,
          isInCart: true
        })),
        status: "Ready"
      };
    }

    case Actions.FetchPageBegin: {
      return {
        ...state,
        isFetchingPage: true
      };
    }
    case Actions.FetchPageFailure: {
      return {
        ...state,
        isFetchingPage: false
      };
    }
    case Actions.FetchPageSuccess: {
      const { page } = action.params as FetchPageBeginActionData;
      const { categories, products } = action.data as FetchPageSuccessActionData;
      return {
        ...state,
        categories,
        items: products.map((item) => ({ ...item, progressButton: null, isInCart: false })),
        page,
        isFetchingPage: false
      };
    }

    case Actions.SetFilter: {
      const { name, value } = action.data as SetFilterActionData;
      return {
        ...state,
        filters: {
          ...state.filters,
          [name]: value
        }
      };
    }

    // TODO: proper implementation. Note: we cannot reference builder here, thus use hardcoded string
    case "CONTENT_ITEM_UPDATE_SETTING": {
      const { name, newValue } = action.data as any;
      if (name in state.filters) {
        return {
          ...state,
          filters: {
            ...state.filters,
            [name]: newValue
          }
        };
      }
    }

    default:
      return state;
  }
};

export const defaultState: ProductList.State = {
  categories: [],
  hasProducts: false,
  id: "",
  orders: [],
  items: [],
  page: 1,
  status: "None",
  isFetchingPage: false,
  cartUrl: "",
  filters: {
    categoryId: null,
    order: "",
    searchText: ""
  },
  shouldDisplayTax: false
};

export const previewState: ProductList.State = {
  categories: [],
  hasProducts: true,
  id: "",
  orders: [],
  items: [
    {
      allowReviews: false,
      availableQuantity: 1,
      hasVariants: false,
      id: "dummy",
      imageUrl: null,
      isInCart: false,
      name: "Dummy",
      permalink: "/dummy",
      price: {
        currencyCode: "USD",
        price: 100,
        salesPrice: 100
      },
      productVariantId: "dummy",
      progressButton: "add",
      taxPercentage: 0
    }
  ],
  page: 1,
  status: Status.Ready,
  isFetchingPage: false,
  cartUrl: "",
  filters: {
    categoryId: null,
    order: "",
    searchText: ""
  },
  shouldDisplayTax: false
};
export default wrapReducer({ defaultState, reducer, previewState });
